import React, { useMemo } from 'react'
import { Box } from '@chakra-ui/react'

import {
    AppointmentFieldsFragment,
    AppointmentStatus,
    TimeInterval,
    TimeSlotFieldsFragment,
    useGetTimeSlotsForClinicForDayQuery,
    useSearchAppointmentsQuery,
} from '~graphql/generated/graphql'
import {
    addHours,
    addMinutes,
    areIntervalsOverlapping,
    format,
    startOfDay,
} from 'date-fns'
import HourSlot from '~components/daily-huddle/daily-huddle-steps/common/appointments/calendar/hour-slot'
import { Flex, Heading } from '@chakra-ui/react'
import { useClinic } from '~contexts/clinic-context'
import { OpenSlotItem } from './open-slot-item'
import { AppointmentItem } from './appointment-item'
import { UnavailableBlock } from './unavailable-item'
import { InsertAppointmentItem } from './insert-appointment-item'
import CenteredSpinner from '~components/shared/centered-spinner'
import { checkForChckupOrigin } from '~utils/helpers'
import { getAppointmentStatusFromAppointment } from '~utils/appointment-helpers'

interface Props {
    appointment: AppointmentFieldsFragment
}

const SchedulePreview: React.FC<Props> = ({ appointment }) => {
    const date = startOfDay(appointment.start_time)
    const start = addMinutes(appointment.start_time, -30)
    const end = addMinutes(
        addMinutes(appointment.start_time, appointment.minutes),
        60,
    )

    const startTime = start.getHours()
    const endTime = end.getHours()

    const { clinic } = useClinic()
    const { data: timeSlotData, loading: timeSlotsLoading } =
        useGetTimeSlotsForClinicForDayQuery({
            variables: {
                clinicId: clinic?.id || '',
                date: start.getTime(),
            },
            skip: !clinic,
        })

    const { data: appointmentData, loading: appointmentsLoading } =
        useSearchAppointmentsQuery({
            variables: {
                searchInput: {
                    clinicId: clinic?.id || '',
                    clinicianId: appointment.clinician?.id || '',
                    start_date: startOfDay(date).getTime(),
                    end_date: addMinutes(startOfDay(date), 24 * 60).getTime(),
                    limit: 100,
                },
            },
        })

    const timeSlots: TimeSlotFieldsFragment[] =
        timeSlotData?.getTimeSlotsForClinicForDay || []

    const openSlots = timeSlots
        .filter(
            s =>
                s.clinicians.some(c => c.id === appointment?.clinician?.id) &&
                new Date(s.start_time).getHours() >= startTime &&
                new Date(s.start_time).getHours() < endTime,
        )
        .map(s => new Date(s.start_time))

    const appointments = useMemo(
        () =>
            (appointmentData?.searchAppointments || []).filter(
                a =>
                    new Date(a.start_time).getHours() >= startTime &&
                    new Date(a.start_time).getHours() < endTime &&
                    a.id !== appointment.id,
            ),
        [appointmentData],
    )

    const unavailableTimes = mapUnavailableTimes(
        date,
        [appointment, ...appointments],
        openSlots,
        startTime,
        endTime,
        clinic?.settings?.time_slot_minutes ?? 30,
    )

    const status = getAppointmentStatusFromAppointment(appointment)

    if (timeSlotsLoading || appointmentsLoading)
        return <CenteredSpinner py={24} />

    return (
        <Box w="full" mt={2} pt={4} borderWidth={1} rounded="xl">
            <Box px={4}>
                <Heading size="md">
                    {format(date, 'eeee, LLL do, yyyy')}
                </Heading>
            </Box>
            <Box w="full" mt={2} pt={4} position="relative">
                {[
                    ...(Array(
                        Math.abs(Math.ceil(endTime + 1 - startTime)),
                    ).keys() || []),
                ].map((hour, i) => {
                    const time = hour + Math.floor(startTime)

                    return (
                        <HourSlot
                            key={time + i}
                            hour={time % 12 || 12}
                            isPM={time >= 12 && time < 24}
                            shouldHideMinutes={time === endTime}
                            isLast={
                                i === Math.abs(Math.ceil(endTime - startTime))
                            }
                        />
                    )
                })}
                <Flex
                    position="absolute"
                    w={{ base: 'calc(100% - 72px)', md: 'calc(100% - 102px)' }}
                    h="calc(100%)"
                    justify="space-between"
                    top="28px"
                    left={{ base: '60px', md: '88px' }}
                    flexDir="column"
                >
                    <Flex flex={1} pos="relative">
                        {openSlots.map((slot, index) => {
                            return (
                                <OpenSlotItem
                                    key={slot.getTime() + '-' + index}
                                    slot={{
                                        start_time: slot.getTime(),
                                        minutes:
                                            clinic?.settings
                                                ?.time_slot_minutes || 30,
                                        clinician: appointment?.clinician!,
                                    }}
                                    startTime={startTime}
                                    index={index}
                                />
                            )
                        })}
                        {unavailableTimes.map((interval, index) => (
                            <UnavailableBlock
                                key={index}
                                block={interval}
                                startTime={startTime}
                                endTime={endTime}
                                index={index}
                            />
                        ))}
                        {[...appointments]
                            .filter(a => a.id !== appointment.id)
                            .filter(a => {
                                const status =
                                    getAppointmentStatusFromAppointment(a)
                                return (
                                    status !== AppointmentStatus.Cancelled &&
                                    status !== AppointmentStatus.Denied
                                )
                            })
                            .map((app, index) => (
                                <AppointmentItem
                                    key={app.start_time + '-' + index}
                                    appointment={app}
                                    startTime={startTime}
                                    index={index}
                                    isConflicting={areIntervalsOverlapping(
                                        {
                                            start: app.start_time,
                                            end: addMinutes(
                                                app.start_time,
                                                app.minutes,
                                            ),
                                        },
                                        {
                                            start: appointment.start_time,
                                            end: addMinutes(
                                                appointment.start_time,
                                                appointment.minutes,
                                            ),
                                        },
                                    )}
                                />
                            ))}
                        {status === AppointmentStatus.Pending &&
                        checkForChckupOrigin(appointment.origin) ? (
                            <InsertAppointmentItem
                                appointment={appointment}
                                startTime={startTime}
                                index={0}
                                hasConflict={appointments.some(app =>
                                    areIntervalsOverlapping(
                                        {
                                            start: app.start_time,
                                            end: addMinutes(
                                                app.start_time,
                                                app.minutes,
                                            ),
                                        },
                                        {
                                            start: appointment.start_time,
                                            end: addMinutes(
                                                appointment.start_time,
                                                appointment.minutes,
                                            ),
                                        },
                                    ),
                                )}
                            />
                        ) : (
                            <AppointmentItem
                                appointment={appointment}
                                startTime={startTime}
                                index={0}
                                isCurrent
                            />
                        )}
                    </Flex>
                </Flex>
            </Box>
        </Box>
    )
}

function mapUnavailableTimes(
    date: Date,
    appointments: AppointmentFieldsFragment[],
    openSlots: Date[],
    startTime: number,
    endTime: number,
    duration: number,
) {
    let unavailableTimes: TimeInterval[] = []
    let appts: TimeInterval[] = appointments.map(a => ({
        start: a.start_time,
        end: addMinutes(a.start_time, a.minutes || 0).getTime(),
    }))
    let slotsAsIntervals: TimeInterval[] = openSlots.map(s => ({
        start: s.getTime(),
        end: addMinutes(s, duration).getTime(),
    }))

    function pushTimeIfLongEnough(interval: TimeInterval) {
        if (
            interval?.end &&
            interval?.start &&
            interval.end - interval.start >= 10 * 60 * 1000
        ) {
            unavailableTimes.push(interval)
        }
    }

    const knownTimes = [...appts, ...slotsAsIntervals].sort(
        (a, b) =>
            (a.start || 0) - (b.start || 0) - ((b.end || 0) - (a.end || 0)),
    )

    const earliestTime = addHours(startOfDay(date), startTime).getTime()

    if (knownTimes?.length > 0) {
        if (earliestTime < (knownTimes[0].start || 0)) {
            pushTimeIfLongEnough({
                start: earliestTime,
                end: knownTimes[0].start,
            })
        }
    } else {
        pushTimeIfLongEnough({
            start: addHours(startOfDay(date), startTime).getTime(),
            end: addHours(startOfDay(date), endTime).getTime(),
        })
    }

    knownTimes.forEach((time, index) => {
        if (!knownTimes[index + 1]) {
            if (
                (time.end || 0) < addHours(startOfDay(date), endTime).getTime()
            ) {
                pushTimeIfLongEnough({
                    start: time.end,
                    end: addHours(startOfDay(date), endTime).getTime(),
                })
            }
        } else {
            const nextTime = knownTimes[index + 1]

            if ((nextTime.start || 0) - (time.end || 0) > 600) {
                pushTimeIfLongEnough({
                    start: time.end,
                    end: nextTime.start,
                })
            }
        }
    })

    return unavailableTimes
}

export default SchedulePreview
